import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Paper, Theme } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { LayoutModel, ModelCategory, ProjectLayoutModel } from '@paragon/contract-library';
import { DataRow, isDataRow, RowWithMetadata } from '@paragon/layout-models';
import { createScrollbarStyling } from '@paragon/themes';

import { selectModel, setPageIndex, toggleSortColumn } from '../../store/actions/grid';
import { isSortableColumn } from '../../store/grid/ordering';
import { PAGE_SIZE } from '../../store/grid/paging';
import { getActivePageIndex, getSortedProjects, getVisiblePageRows } from '../../store/selectors/projectGridRows';
import { AssigneeAvatar } from '../AssigneeAvatar';
import { DueDateText } from '../DueDateText';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ScrollOnHoverText, scrollTextOnHoverStyles } from '../ScrollOnHoverText';
import { DateWithoutTimeFormatter } from './DateWithoutTimeFormatter';
import { DesignProjectButton } from './DesignProjectButton';
import { ProjectGridExpandCollapseButton } from './ProjectGridExpandCollapseButton';
import { ProjectGridQuantityCell } from './ProjectGridQuantityCell';
import { ProjectGridRowCheckbox, revealRowCheckboxOnHoverStyles } from './ProjectGridRowCheckbox';
import { ProjectGridSealedCell } from './ProjectGridSealedCell';

const designButtonClassName = 'project-row-design-button';
const selectedRowClassName = 'project-row-selected';
const checkboxCellClassName = 'project-row-checkbox-cell';
const checkboxField = 'rowCheckbox';

const MIN_COLUMN_WIDTH = 60;

// A click selects the whole row, so the outline the grid draws around the cell that took the click points at
//   something that is not the unit of selection here.
const focusOutlineSelector = [gridClasses.cell, gridClasses.columnHeader]
  .flatMap((className) => [`& .${className}:focus`, `& .${className}:focus-within`])
  .join(', ');

const revealDesignButtonOnHoverStyles = {
  [`&:hover .${designButtonClassName}, &:focus-within .${designButtonClassName}`]: {
    width: 'auto',
    marginLeft: 1,
    overflow: 'visible',
  },
} as const;

function getLayoutModelRow(params: GridRenderCellParams<RowWithMetadata>): DataRow<LayoutModel> {
  return params.row.row as DataRow<LayoutModel>;
}

/** Renders a project-only value, since the component rows beneath a project have nothing to show in these columns. */
function renderProjectCell(
  params: GridRenderCellParams<RowWithMetadata>,
  getValue: (project: ProjectLayoutModel) => React.ReactNode,
) {
  const row = getLayoutModelRow(params);
  if (row.item.category !== ModelCategory.Project) return null;
  return <span>{getValue(row.item as ProjectLayoutModel)}</span>;
}

const columnDefinitions: readonly GridColDef<RowWithMetadata>[] = [
  {
    field: checkboxField,
    headerName: '',
    width: 48,
    minWidth: 48,
    resizable: false,
    align: 'center',
    cellClassName: checkboxCellClassName,
    renderCell: (params) => <ProjectGridRowCheckbox rowId={params.row.row.rowId} />,
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 130,
    renderCell: (params) => <DateWithoutTimeFormatter row={getLayoutModelRow(params)} />,
  },
  {
    field: 'name',
    headerName: 'Name',
    width: 720,
    renderHeader: () => (
      <>
        {/* The marginLeft is the size of the collapse/expand icon, aligning "Name" with the values below. */}
        <Box component="span" sx={{ marginLeft: '40px' }} />
        Name
      </>
    ),
    renderCell: (params) => {
      const rowWithMetadata = params.row;
      const row = getLayoutModelRow(params);
      const isProject = row.item.category === ModelCategory.Project;

      return (
        <Box component="span" sx={{ alignItems: 'center', display: 'flex', minWidth: 0, width: '100%' }}>
          <Box component="span" sx={{ marginX: `${rowWithMetadata.indentationLevel * 24}px` }} />
          {rowWithMetadata.expandable ? (
            <ProjectGridExpandCollapseButton rowId={rowWithMetadata.row.rowId} />
          ) : (
            // The marginLeft is the size of the collapse/expand icon.
            <Box component="span" sx={{ marginLeft: '40px' }} />
          )}
          <ScrollOnHoverText sx={{ flex: 1 }}>{rowWithMetadata.row.name}</ScrollOnHoverText>
          {isProject && (
            <Box
              component="span"
              className={designButtonClassName}
              sx={{
                display: 'inline-flex',
                flexShrink: 0,
                marginLeft: 1,
                '@media (hover: hover)': { width: 0, marginLeft: 0, overflow: 'hidden' },
              }}
            >
              <DesignProjectButton
                projectGuid={row.item.guid}
                size="small"
                sx={{
                  boxShadow: 1,
                  '&:hover, &.Mui-focusVisible': { boxShadow: 2 },
                }}
              />
            </Box>
          )}
        </Box>
      );
    },
  },
  {
    field: 'contact',
    headerName: 'Contact',
    width: 160,
    renderCell: (params) => renderProjectCell(params, (project) => project.contactName),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    renderCell: (params) => renderProjectCell(params, (project) => project.status),
  },
  {
    field: 'contactEmail',
    headerName: 'Contact Email',
    width: 200,
    renderCell: (params) => renderProjectCell(params, (project) => project.contactEmail),
  },
  {
    field: 'assignee',
    headerName: 'Assignee',
    width: 180,
    renderCell: (params) => {
      const row = getLayoutModelRow(params);
      if (row.item.category !== ModelCategory.Project) return null;
      return <AssigneeAvatar assigneeEmail={(row.item as ProjectLayoutModel).assigneeEmail} />;
    },
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    width: 130,
    renderCell: (params) => {
      const row = getLayoutModelRow(params);
      if (row.item.category !== ModelCategory.Project) return null;
      return <DueDateText dueDate={(row.item as ProjectLayoutModel).dueDate} />;
    },
  },
  {
    field: 'quantity',
    headerName: 'Quantity',
    width: 90,
    renderCell: (params) => <ProjectGridQuantityCell row={getLayoutModelRow(params)} />,
  },
  {
    field: 'sealed',
    headerName: 'Sealed',
    width: 90,
    renderCell: (params) => <ProjectGridSealedCell row={getLayoutModelRow(params)} />,
  },
];

/**
 * Only the columns the store knows how to order by are sortable; the rest are derived or project-only values.
 *
 * Every column but the checkbox also claims a share of the row proportional to its own width, so that together they
 * fill the viewport the way the old fixed-layout table did, shrinking with it down to `MIN_COLUMN_WIDTH`.
 */
const columns: readonly GridColDef<RowWithMetadata>[] = columnDefinitions.map((column) => ({
  ...column,
  sortable: isSortableColumn(column.field),
  ...(column.field === checkboxField ? null : { flex: column.width, minWidth: MIN_COLUMN_WIDTH }),
}));

const gridStyles = (theme: Theme) => ({
  border: 'none',
  [`& .${gridClasses.scrollbar}`]: createScrollbarStyling(theme),
  [focusOutlineSelector]: { outline: 'none' },
  // The checkbox is 38px wide, and the 10px of padding the cell carries on either side leaves it only 28px.
  [`& .${checkboxCellClassName}`]: { padding: 0 },
  [`& .${gridClasses.row}`]: {
    cursor: 'pointer',
    ...revealRowCheckboxOnHoverStyles,
    ...revealDesignButtonOnHoverStyles,
    ...scrollTextOnHoverStyles,
  },
  [`& .${gridClasses.row}.${selectedRowClassName}`]: {
    bgcolor: 'selectedList.main',
    '&:hover': { bgcolor: 'selectedList.main' },
  },
});

const loadingSlotProps = { basePagination: { material: { labelDisplayedRows: () => 'loading...' } } };
const loadingLocaleText = { noRowsLabel: 'Loading...' };

export function ProjectGrid() {
  const dispatch = useAppDispatch();
  const allDataLoaded = useAppSelector((state) => state.application.allDataLoaded);
  const order = useAppSelector((state) => state.grid.order);
  const orderByColumnName = useAppSelector((state) => state.grid.orderByColumnName);
  const pageIndex = useAppSelector((state) => state.grid.pageIndex);
  const selectedPath = useAppSelector((state) => state.grid.selectedPath);
  const activePage = useAppSelector(getActivePageIndex);
  const visibleRows = useAppSelector(getVisiblePageRows);
  const projectsCount = useAppSelector((state) => getSortedProjects(state).length);

  useEffect(() => {
    if (activePage !== pageIndex) dispatch(setPageIndex(activePage));
  }, [activePage, pageIndex, dispatch]);

  const sortModel: GridSortModel = useMemo(
    () => [{ field: orderByColumnName, sort: order }],
    [order, orderByColumnName],
  );

  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      // `sortingOrder` below rules out the grid's third, unsorted state, but guard anyway: the store has no way to
      //   represent "no column sorted".
      const columnName = newSortModel[0]?.field;
      if (columnName != null && isSortableColumn(columnName)) dispatch(toggleSortColumn(columnName));
    },
    [dispatch],
  );

  const paginationModel = useMemo(() => ({ page: activePage, pageSize: PAGE_SIZE }), [activePage]);

  return (
    <Paper elevation={0} sx={expandToFillParentStyles.containerStyleProps}>
      <Box sx={expandToFillParentStyles.targetStyleProps}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          getRowId={(rowWithMetadata: RowWithMetadata) => rowWithMetadata.row.rowId}
          getRowClassName={(params) => (params.id === selectedPath ? selectedRowClassName : '')}
          onRowClick={(params) => {
            const row = params.row.row;
            dispatch(selectModel(row.rowId, row.parent && isDataRow(row.parent) ? row.parent.id : null));
          }}
          // The rows are one page of a flattened tree that the store has already sorted and sliced, so the grid must
          //   leave both alone: sorting its own copy would scatter child rows away from their parents.
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortModelChange}
          sortingOrder={['asc', 'desc']}
          paginationMode="server"
          rowCount={projectsCount}
          paginationModel={paginationModel}
          onPaginationModelChange={(newPaginationModel) => {
            dispatch(setPageIndex(newPaginationModel.page));
          }}
          pageSizeOptions={[PAGE_SIZE]}
          // Projects stream in a page at a time, so until they have all arrived neither the total in the footer nor
          //   an empty grid means what it says.
          slotProps={allDataLoaded ? undefined : loadingSlotProps}
          localeText={allDataLoaded ? undefined : loadingLocaleText}
          rowSelection={false}
          disableColumnFilter
          disableColumnMenu
          sx={[gridStyles, { height: '100%' }]}
        />
      </Box>
    </Paper>
  );
}
